"use client";

import {
  BookOpen,
  FileText,
  HelpCircle,
  PenSquare,
  PlayCircle,
  StickyNote,
  Volume2,
} from "lucide-react";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  getRolePlayMode,
  getRolePlayModeLabelWithScore,
} from "@/lib/simulationMode";
import ModePill from "@/components/simulations/ModePill";
import AttitudePill from "@/components/personas/AttitudePill";

export const CONTENT_TYPE_ORDER = [
  "RESOURCE",
  "TEST",
  "ROLEPLAY",
  "STORY",
  "FAQ",
  "GLOSSARY",
];

const UNKNOWN_CONTENT_META = {
  icon: FileText,
  label: "Content",
  shortLabel: "Content",
  navLabel: "Content",
  pluralLabel: "Content",
  tone: "gray",
  iconClass: "text-gray-500",
  tile: "bg-gray-100 text-gray-500",
  pickerCard: "border-gray-100 hover:border-gray-300 hover:ring-gray-200",
  pickerHoverBg: "hover:bg-gray-50",
  pill: "border-gray-200 bg-gray-50 text-gray-600",
};

// Single source of truth for learning-path content-type identity and color.
// `tone` is the lib/ui/tones.js hue, for pieces that read a tone (PickerRow).
export const CONTENT_META = {
  RESOURCE: {
    icon: BookOpen,
    label: "Resource",
    shortLabel: "Resource",
    navLabel: "Resource",
    pluralLabel: "Resources",
    tone: "action",
    iconClass: "text-action-600",
    tile: "bg-action-50 text-action-600",
    pickerCard: "border-action-100 hover:border-action-300 hover:ring-action-200",
    pickerHoverBg: "hover:bg-action-50/50",
    pill: "border-action-200 bg-action-50/60 text-action-700",
  },
  TEST: {
    icon: FileText,
    label: "Knowledge test",
    shortLabel: "Test",
    navLabel: "Test",
    pluralLabel: "Tests",
    tone: "emerald",
    iconClass: "text-emerald-600",
    tile: "bg-emerald-50 text-emerald-600",
    pickerCard:
      "border-emerald-100 hover:border-emerald-300 hover:ring-emerald-200",
    pickerHoverBg: "hover:bg-emerald-50/50",
    pill: "border-emerald-200 bg-emerald-50/60 text-emerald-700",
  },
  ROLEPLAY: {
    icon: PlayCircle,
    label: "Assignment",
    shortLabel: "Assignment",
    navLabel: "Assignment",
    pluralLabel: "Assignments",
    tone: "violet",
    iconClass: "text-violet-600",
    tile: "bg-violet-50 text-violet-600",
    pickerCard:
      "border-violet-100 hover:border-violet-300 hover:ring-violet-200",
    pickerHoverBg: "hover:bg-violet-50/50",
    pill: "border-violet-200 bg-violet-50/60 text-violet-700",
  },
  STORY: {
    icon: PenSquare,
    label: "Story assignment",
    shortLabel: "Story",
    navLabel: "Story Assignment",
    pluralLabel: "Stories",
    tone: "warning",
    iconClass: "text-warning-600",
    tile: "bg-warning-50 text-warning-600",
    pickerCard:
      "border-warning-100 hover:border-warning-300 hover:ring-warning-200",
    pickerHoverBg: "hover:bg-warning-50/50",
    pill: "border-warning-200 bg-warning-50/60 text-warning-700",
  },
  FAQ: {
    icon: HelpCircle,
    label: "Q&As",
    shortLabel: "Q&As",
    navLabel: "Q&As",
    pluralLabel: "Q&As",
    tone: "teal",
    iconClass: "text-teal-600",
    tile: "bg-teal-50 text-teal-600",
    pickerCard: "border-teal-100 hover:border-teal-300 hover:ring-teal-200",
    pickerHoverBg: "hover:bg-teal-50/50",
    pill: "border-teal-200 bg-teal-50/60 text-teal-700",
  },
  GLOSSARY: {
    icon: Volume2,
    label: "Glossary",
    shortLabel: "Glossary",
    navLabel: "Glossary",
    pluralLabel: "Glossary",
    tone: "indigo",
    iconClass: "text-indigo-600",
    tile: "bg-indigo-50 text-indigo-600",
    pickerCard:
      "border-indigo-100 hover:border-indigo-300 hover:ring-indigo-200",
    pickerHoverBg: "hover:bg-indigo-50/50",
    pill: "border-indigo-200 bg-indigo-50/60 text-indigo-700",
  },
};

export function getContentMeta(type) {
  return CONTENT_META[type] || UNKNOWN_CONTENT_META;
}

export function ContentTypeIcon({
  type,
  status,
  completedOnly = false,
  iconSize = "h-3.5 w-3.5",
  className,
  tooltip,
}) {
  const meta = getContentMeta(type);
  const Icon = meta.icon;
  const colorClass =
    completedOnly && status !== "COMPLETED"
      ? "text-gray-400"
      : meta.iconClass;

  return (
    <SimpleTooltip content={tooltip || meta.shortLabel}>
      <span className="inline-flex shrink-0">
        <Icon className={cn(iconSize, colorClass, className)} aria-hidden />
      </span>
    </SimpleTooltip>
  );
}

// The kind's name as text, for a server component that cannot call
// getContentMeta across the client boundary.
export function ContentTypeLabel({ type }) {
  return getContentMeta(type).label;
}

/**
 * Tinted, rounded icon tile for a content item.
 * size/iconSize let callers use a smaller tile in the compact preview list.
 */
export function ContentTypeTile({
  type,
  size = "h-9 w-9",
  iconSize = "h-[18px] w-[18px]",
}) {
  const meta = getContentMeta(type);
  const Icon = meta.icon;
  return (
    <span
      className={cn(
        "grid shrink-0 place-items-center rounded-lg",
        size,
        meta.tile
      )}
    >
      <Icon className={iconSize} strokeWidth={1.75} />
    </span>
  );
}

/**
 * Status pills for a role-play content item: its mode (Scored · N% / Live Mic
 * / Practice) and the buyer's attitude. `size` is StatusPill's.
 */
export function RolePlayPills({ rolePlay, size = "default" }) {
  if (!rolePlay) return null;
  return (
    <>
      <ModePill
        mode={getRolePlayMode(rolePlay)}
        label={getRolePlayModeLabelWithScore(rolePlay)}
        size={size}
        variant="pill"
        className="whitespace-nowrap"
      />
      <AttitudePill
        value={rolePlay.mentalState}
        size={size}
        className="whitespace-nowrap"
      />
    </>
  );
}

export { StickyNote };
